import { clsx } from "clsx";
import { type ReactElement, useState } from "react";
import Measure from "react-measure";
import type { To } from "react-router";

import { SymbolIcon, TimeAgo } from "widgets";

import style from "./styles/Tiles.module.scss";

import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import { getTimeTooltipMessage } from "@/ui-modules/core/tiles/utils/getTimeTooltipMessage.ts";
import type { Project, SampleStatus, User } from "../../graphql.js";
import { RouterLink } from "./RouterLink.js";
import { SmartLink } from "./SmartLink.js";
import { Avatar, AvatarPile } from "./UserAvatar.js";
import { TooltipController } from "./overlays/components/OverlayControllers.js";

/* --- Generic Tile --- */
export interface TimeDetailsProps {
  creator?: User;
  creationDate?: string;
  modder?: User;
  modificationDate?: string;
  lastUseDate?: string;
}

export interface GenericTileProps {
  type?: string;
  project?: Project;
  title: string;
  description?: string | ReactElement;
  timeDetails?: TimeDetailsProps;
  metadata?: number;
  link?: To;
  icon?: ReactElement;
  onClick?: (...args: any) => void;
  private?: boolean;
  className?: string;
}

export function GenericTile(props: GenericTileProps) {
  const [width, setWidth] = useState<number>(0);

  return (
    <Measure
      client
      onResize={({ client }) => {
        setWidth(client?.width ?? 0);
      }}
    >
      {({ measureRef }) => (
        <RouterLink
          to={props.link}
          role="button"
          tabIndex={-1}
          forwardedRef={measureRef}
          data-meta={props.metadata}
          className={clsx(
            style.tile,
            {
              [style.is_interactive]: props.link || props.onClick,
              [style.mini_no_project]: !props.project,
              [style.mini]: width < 560,
              [style.medium]: width >= 560 && width < 800,
              [style.large]: width >= 800,
            },
            props.className,
          )}
          onClick={() => {
            props.onClick?.();
          }}
        >
          <div className={style.title}>
            {props.title}
            {props.private && (
              <SymbolIcon icon={"lock"} className={style.title_icon} aria-label={"(private)"} size={11} />
            )}
          </div>
          <div className={style.type}>{props.type}</div>

          {props.project && (
            <div className={style.project}>
              <ProjectColoredLink project={props.project} noLink />
            </div>
          )}

          {props.description && <div className={style.description}>{props.description}</div>}

          {props.timeDetails &&
            (props.timeDetails.lastUseDate || props.timeDetails.modificationDate || props.timeDetails.creationDate) && (
              <div className={style.date}>
                <TimeAgo
                  dateTimeISO={
                    props.timeDetails.lastUseDate ||
                    props.timeDetails.modificationDate ||
                    props.timeDetails.creationDate
                  }
                />
              </div>
            )}

          {props.timeDetails && (props.timeDetails.creator || props.timeDetails.modder) && (
            <TooltipController content={getTimeTooltipMessage(props.timeDetails)}>
              {(tooltipTriggerProps) => (
                <div className={style.people} {...tooltipTriggerProps}>
                  <AvatarPile>
                    <Avatar user={props.timeDetails.creator} />
                    {props.timeDetails.modificationDate != props.timeDetails.creationDate && (
                      <Avatar user={props.timeDetails.modder} />
                    )}
                  </AvatarPile>
                </div>
              )}
            </TooltipController>
          )}

          {props.icon && (
            <div className={style.icon} aria-hidden="true">
              {props.icon}
            </div>
          )}
        </RouterLink>
      )}
    </Measure>
  );
}

/* --- Simple Tile - Single column --- */
interface SimpleTileProps {
  title: string;
  description?: string | ReactElement;
  time?: string;
  link?: string;
  onClick?: (...any) => void;
  className?: string;
  metadata?: number;
  project?: Project;
}

export function SimpleTile(props: SimpleTileProps) {
  return (
    <RouterLink
      to={props.link}
      data-meta={props.metadata}
      className={clsx(
        style.tile,
        style.simple,
        style.mini,
        { [style.is_interactive]: props.link || props.onClick },
        props.className,
      )}
      onClick={() => props.onClick && props.onClick()}
    >
      <div className={style.title}>{props.project ? <ProjectColoredLink project={props.project} /> : props.title}</div>
      {props.description && <div className={style.description}>{props.description}</div>}
      {props.time && (
        <div className={style.date}>
          <TimeAgo dateTimeISO={props.time} />
        </div>
      )}
    </RouterLink>
  );
}

/* --- Statistical Tile --- */
interface StatProps {
  name?: string;
  value: number;
  link?: To;
  onClick?: any;
}

interface StatisticalTileProps {
  time?: string;
  status?: SampleStatus;
  headStat?: StatProps;
  footStat?: StatProps;
  className?: string;
}

export function StatisticalTile(props: StatisticalTileProps) {
  return (
    <RouterLink
      to={props.headStat?.link}
      data-meta={props.headStat?.value}
      className={clsx(style.tile, style.statistical, { [style.is_interactive]: props.headStat?.link }, props.className)}
    >
      <TooltipController
        content={
          <>
            {props.status.description && (
              <div>
                <strong>{props.status.description}</strong>
              </div>
            )}
            <div>Class: {props.status.statusClass.name}</div>
          </>
        }
      >
        {(tooltipTriggerProps) => (
          <span {...tooltipTriggerProps}>
            <div className={style.title}>{props.status?.name}</div>
          </span>
        )}
      </TooltipController>

      {props.headStat && (
        <div className={style.head}>
          <span className={clsx(style.head_value, { [style.head_mini_value]: props.headStat.value > 9999 })}>
            {props.headStat.value}
          </span>
          <span className={style.head_name}>{props.headStat.name}</span>
        </div>
      )}

      {props.footStat &&
        (props.footStat.link ? (
          <SmartLink to={props.footStat.link} className={style.foot} onClick={(e) => e.stopPropagation()}>
            <span className={style.foot_value}>{props.footStat.value}</span>
            <span className={style.foot_name}>{props.footStat.name}</span>
          </SmartLink>
        ) : (
          <div className={style.foot} onClick={props.headStat.onClick}>
            <span className={style.foot_value}>{props.footStat.value}</span>
            <span className={style.foot_name}>{props.footStat.name}</span>
          </div>
        ))}

      <div className={style.date}>{props.time && <TimeAgo dateTimeISO={props.time} />}</div>
    </RouterLink>
  );
}
